export const SHARING_REQUEST_STATUS = {
    PENDING: 'Pending',
    ACCEPTED: 'Accepted',
    REJECTED: 'Rejected',
    MIXED: 'Mixed'
};

export const SHARING_REQUEST_FILTERS = [
    SHARING_REQUEST_STATUS.PENDING,
    SHARING_REQUEST_STATUS.ACCEPTED,
    SHARING_REQUEST_STATUS.REJECTED,
    SHARING_REQUEST_STATUS.MIXED,
    'All'
];

export const SHARING_SOURCE = {
    V1: 'v1',
    V2: 'v2'
};

export const SHARING_TYPE_LABEL = {
    [SHARING_SOURCE.V1]: 'Connection (legacy)',
    [SHARING_SOURCE.V2]: 'Sharing Agreement'
};

export function typeLabelForSource(source) {
    return SHARING_TYPE_LABEL[source] || '';
}

const EVENT_LABELS = {
    'com.trimble.trans.track.position.event': 'Position',
    'com.trimble.trans.track.location.event': 'Location',
    'com.trimble.trans.track.shipment.event': 'Shipment',
    'com.trimble.trans.track.stopstatus.event': 'Stop Status'
};

export function titleStatus(value) {
    const token = String(value || '')
        .trim()
        .toLowerCase();
    if (token === 'pending') {
        return SHARING_REQUEST_STATUS.PENDING;
    }
    if (token === 'accepted') {
        return SHARING_REQUEST_STATUS.ACCEPTED;
    }
    if (token === 'rejected') {
        return SHARING_REQUEST_STATUS.REJECTED;
    }
    if (token === 'mixed') {
        return SHARING_REQUEST_STATUS.MIXED;
    }
    return null;
}

export function rollupGroupStatuses(statuses) {
    const normalized = (statuses || []).map(titleStatus).filter(Boolean);
    if (normalized.length === 0) {
        return SHARING_REQUEST_STATUS.PENDING;
    }
    if (normalized.includes(SHARING_REQUEST_STATUS.PENDING)) {
        return SHARING_REQUEST_STATUS.PENDING;
    }
    const hasAccepted = normalized.includes(SHARING_REQUEST_STATUS.ACCEPTED);
    const hasRejected = normalized.includes(SHARING_REQUEST_STATUS.REJECTED);
    if (hasAccepted && hasRejected) {
        return SHARING_REQUEST_STATUS.MIXED;
    }
    if (hasRejected) {
        return SHARING_REQUEST_STATUS.REJECTED;
    }
    return SHARING_REQUEST_STATUS.ACCEPTED;
}

function countLabel(count, singular, plural) {
    return `${count} ${count === 1 ? singular : plural}`;
}

export function sharingSummary(eventLabels) {
    const labels = [
        ...new Set((eventLabels || []).filter((label) => label && String(label).trim()))
    ];
    labels.sort((left, right) => left.localeCompare(right));
    return labels.join(', ');
}

function eventLabelFromName(eventName) {
    const name = String(eventName || '').trim();
    if (!name) {
        return '';
    }
    return EVENT_LABELS[name] || name;
}

function eventLabelsFromConnection(connection) {
    const labels = [];
    const collect = (value) => {
        if (!value) {
            return;
        }
        if (typeof value === 'string') {
            const label = eventLabelFromName(value);
            if (label) {
                labels.push(label);
            }
            return;
        }
        if (Array.isArray(value)) {
            value.forEach(collect);
            return;
        }
        if (typeof value === 'object') {
            if (value.name || value.eventType || value.event_type) {
                collect(value.name || value.eventType || value.event_type);
            }
            if (Array.isArray(value.event_types)) {
                collect(value.event_types);
            }
            if (Array.isArray(value.eventTypes)) {
                collect(value.eventTypes);
            }
        }
    };

    collect(connection?.event_types);
    collect(connection?.eventTypes);
    collect(connection?.events);
    return labels;
}

export function pendingGroupsSubtitle(pendingGroupCount) {
    const count = Number(pendingGroupCount) || 0;
    if (count <= 0) {
        return '';
    }
    return `${countLabel(count, 'group', 'groups')} still pending`;
}

export function carrierNameFromRecord(record) {
    if (!record || typeof record !== 'object') {
        return '';
    }
    const sourceName = record.source && typeof record.source === 'object' ? record.source.name : '';
    return (
        sourceName ||
        record.customerAccountName ||
        record.carrierName ||
        record.carrier_name ||
        record.source_name ||
        ''
    );
}

function eventLabelsForMessageTypes(messageTypes) {
    if (!messageTypes || typeof messageTypes !== 'object') {
        return [];
    }
    const labels = Object.keys(messageTypes)
        .filter((key) => Array.isArray(messageTypes[key]) && messageTypes[key].length > 0)
        .map((key) => EVENT_LABELS[key] || key);
    return [...new Set(labels)].sort((left, right) => left.localeCompare(right));
}

function v2Groups(agreement) {
    const groups = [];
    const messageTypes = agreement?.messageTypes;
    if (!messageTypes || typeof messageTypes !== 'object') {
        return groups;
    }
    Object.values(messageTypes).forEach((eventGroups) => {
        (eventGroups || []).forEach((group) => {
            groups.push(group);
        });
    });
    return groups;
}

function v1GroupStatuses(groups) {
    if (!groups || typeof groups !== 'object') {
        return null;
    }
    const statuses = Object.values(groups).map((groupItems) => {
        const items = Array.isArray(groupItems) ? groupItems : [];
        return items[0]?.request_status;
    });
    return statuses.length > 0 ? statuses : null;
}

export function fromV1Connection(connection, groups) {
    const connectionStatus =
        titleStatus(connection?.request_status) || SHARING_REQUEST_STATUS.PENDING;
    const groupStatuses = v1GroupStatuses(groups);
    const pending = Number(connection?.sharing_agreement_count?.pending) || 0;
    const acceptedCount = connection?.sharing_agreement_count?.accepted;
    const rejectedCount = connection?.sharing_agreement_count?.rejected;
    const accepted = acceptedCount == null ? null : Number(acceptedCount) || 0;
    const rejected = rejectedCount == null ? null : Number(rejectedCount) || 0;

    let status = connectionStatus;
    let pendingGroupCount = 0;

    if (connectionStatus === SHARING_REQUEST_STATUS.REJECTED) {
        status = SHARING_REQUEST_STATUS.REJECTED;
    } else if (connectionStatus === SHARING_REQUEST_STATUS.PENDING) {
        status = SHARING_REQUEST_STATUS.PENDING;
    } else if (groupStatuses) {
        status = rollupGroupStatuses(groupStatuses);
        if (status === SHARING_REQUEST_STATUS.PENDING) {
            pendingGroupCount = groupStatuses.filter(
                (groupStatus) => titleStatus(groupStatus) === SHARING_REQUEST_STATUS.PENDING
            ).length;
        }
    } else if (pending > 0) {
        status = SHARING_REQUEST_STATUS.PENDING;
        pendingGroupCount = pending;
    } else if (accepted != null && rejected != null && accepted > 0 && rejected > 0) {
        status = SHARING_REQUEST_STATUS.MIXED;
    } else if (rejected != null && rejected > 0 && accepted === 0) {
        status = SHARING_REQUEST_STATUS.REJECTED;
    } else {
        status = SHARING_REQUEST_STATUS.ACCEPTED;
    }

    return {
        id: String(connection?.id ?? ''),
        source: SHARING_SOURCE.V1,
        typeLabel: typeLabelForSource(SHARING_SOURCE.V1),
        carrierName: carrierNameFromRecord(connection),
        direction: 'Outbound',
        status,
        pendingGroupCount,
        summary: sharingSummary(eventLabelsFromConnection(connection)),
        canDecideWholeRequest: connectionStatus === SHARING_REQUEST_STATUS.PENDING
    };
}

export function fromV2Agreement(agreement) {
    const groups = v2Groups(agreement);
    const status = rollupGroupStatuses(groups.map((group) => group?.respondedStatus));
    const hasNonPending = groups.some((group) => {
        const groupStatus = titleStatus(group?.respondedStatus);
        return groupStatus && groupStatus !== SHARING_REQUEST_STATUS.PENDING;
    });
    const pendingGroupCount =
        status === SHARING_REQUEST_STATUS.PENDING && hasNonPending
            ? groups.filter(
                  (group) => titleStatus(group?.respondedStatus) === SHARING_REQUEST_STATUS.PENDING
              ).length
            : 0;

    return {
        id: String(agreement?.id ?? ''),
        source: SHARING_SOURCE.V2,
        typeLabel: typeLabelForSource(SHARING_SOURCE.V2),
        carrierName: carrierNameFromRecord(agreement) || agreement?.customerAccount || '',
        direction: agreement?.direction || '',
        status,
        pendingGroupCount,
        summary: sharingSummary(eventLabelsForMessageTypes(agreement?.messageTypes)),
        canDecideWholeRequest:
            status === SHARING_REQUEST_STATUS.PENDING &&
            groups.some((group) => isPendingGroupStatus(group?.respondedStatus))
    };
}

export function normalizeSharingRequests({ v1Connections = [], v2Agreements = [] } = {}) {
    return [...v1Connections.map(fromV1Connection), ...v2Agreements.map(fromV2Agreement)];
}

export function filterSharingRequests(
    rows,
    { status = SHARING_REQUEST_STATUS.PENDING, carrier = '' } = {}
) {
    const query = String(carrier || '')
        .trim()
        .toLowerCase();
    return (rows || []).filter((row) => {
        const statusMatch = status === 'All' || row.status === status;
        const carrierMatch =
            !query ||
            String(row.carrierName || '')
                .toLowerCase()
                .includes(query);
        return statusMatch && carrierMatch;
    });
}

export function sortSharingRequests(rows) {
    return [...(rows || [])].sort((left, right) => {
        const byCarrier = String(left.carrierName || '').localeCompare(
            String(right.carrierName || '')
        );
        if (byCarrier !== 0) {
            return byCarrier;
        }
        const byType = String(left.typeLabel || '').localeCompare(String(right.typeLabel || ''));
        if (byType !== 0) {
            return byType;
        }
        return String(left.direction || '').localeCompare(String(right.direction || ''));
    });
}

export function countByStatus(rows) {
    const counts = {
        [SHARING_REQUEST_STATUS.PENDING]: 0,
        [SHARING_REQUEST_STATUS.ACCEPTED]: 0,
        [SHARING_REQUEST_STATUS.REJECTED]: 0,
        [SHARING_REQUEST_STATUS.MIXED]: 0,
        All: (rows || []).length
    };
    (rows || []).forEach((row) => {
        if (counts[row.status] != null) {
            counts[row.status] += 1;
        }
    });
    return counts;
}

export function rowsMatchingCarrier(rows, carrier) {
    return filterSharingRequests(rows, { status: 'All', carrier });
}

function isPendingGroupStatus(value) {
    const status = titleStatus(value);
    return status == null || status === SHARING_REQUEST_STATUS.PENDING;
}

/**
 * Updates respondedStatus on V2 message-type groups.
 * @param {object} agreement
 * @param {string} respondedStatus
 * @param {{ allPending?: boolean, eventType?: string, groupIndex?: number }} [target]
 *        Pass `{ allPending: true }` for accept/decline all pending.
 *        Pass `{ eventType, groupIndex }` to change exactly one group row.
 */
export function withV2RespondedStatus(agreement, respondedStatus, target = { allPending: true }) {
    const allPending = Boolean(target?.allPending);
    const eventType = target?.eventType;
    const groupIndex = target?.groupIndex;
    const messageTypes = {};
    Object.entries(agreement?.messageTypes || {}).forEach(([eventKey, groups]) => {
        messageTypes[eventKey] = (groups || []).map((group, index) => {
            if (allPending) {
                if (!isPendingGroupStatus(group?.respondedStatus)) {
                    return group;
                }
                return { ...group, respondedStatus };
            }
            if (eventKey !== eventType || index !== groupIndex) {
                return group;
            }
            return { ...group, respondedStatus };
        });
    });
    return { ...agreement, messageTypes };
}

export function sharingDetailLocation(row, listState = {}) {
    return {
        pathname: `/sharing-agreements/${encodeURIComponent(row.id)}`,
        search: `?source=${row.source}`,
        state: {
            statusFilter: listState.statusFilter,
            searchValue: listState.searchValue,
            carrierName: row.carrierName
        }
    };
}

export function emptyFilterMessage(status) {
    if (status === SHARING_REQUEST_STATUS.ACCEPTED) {
        return 'No accepted sharing agreements';
    }
    if (status === SHARING_REQUEST_STATUS.REJECTED) {
        return 'No rejected sharing agreements';
    }
    if (status === SHARING_REQUEST_STATUS.MIXED) {
        return 'No mixed sharing agreements';
    }
    if (status === 'All') {
        return 'No sharing agreements';
    }
    return 'No pending sharing agreements';
}

export function reportStatusParam(statusFilter) {
    if (statusFilter === SHARING_REQUEST_STATUS.ACCEPTED) {
        return 'ACCEPTED';
    }
    if (statusFilter === SHARING_REQUEST_STATUS.REJECTED) {
        return 'REJECTED';
    }
    if (statusFilter === SHARING_REQUEST_STATUS.PENDING) {
        return 'PENDING';
    }
    return undefined;
}
